import { SiteConfig } from '../landing/models';
export const STORAGE_KEY = 'maincode_prospeccao_leads_v2';
export type SiteState = 'unknown' | 'no' | 'yes';
export type Status = 'novo' | 'contatado' | 'respondeu' | 'proposta' | 'perdido';
export type OfflineAction = 'none' | 'call' | 'visit' | 'done';
export interface Contactability { status: 'unknown' | 'available' | 'not_found'; checkedAt?: number; lastError?: string | null; lastErrorAt?: number | null }
export interface Lead {
  id: string;
  mapsUrl: string;
  phone: string;
  name: string;
  address: string;
  siteState: SiteState;
  website: string;
  domain?: string;
  websiteAssessment?: { kind:'none'|'external'|'brand'|'unconfirmed';domain:string;listedUrl:string };
  siteReview?: 'pending' | 'approved' | 'rejected';
  captureSource?: { campaignId: string; niche: string; region: string };
  problems: string;
  improvements: string;
  status: Status;
  messages: string[];
  createdAt: string;
  contactability?: Contactability;
  offlineAction?: OfflineAction;
  approachTemplateId?: string;
  approachTemplateVersion?: number;
  replyAudioId?: string;
  site?: Partial<SiteConfig>;
  analysis?: { url: string; at: string; scores: Record<string, number>; findings: string[] };
}
export type Draft = Pick<Lead, 'mapsUrl' | 'phone' | 'name' | 'address' | 'siteState' | 'website' | 'domain' | 'problems' | 'improvements' | 'analysis' | 'site'>;
export function contactBlocked(lead: Lead) { return lead.siteReview === 'pending' || lead.siteReview === 'rejected'; }
export function canQueueWhatsApp(lead: Lead) { return !contactBlocked(lead) && lead.status === 'novo' && Boolean(lead.phone) && lead.contactability?.status !== 'not_found'; }
export const emptyDraft: Draft = { mapsUrl: '', phone: '', name: '', address: '', siteState: 'unknown', website: '', problems: '', improvements: '' };

export function webUrl(value: string): string {
  const url = new URL(/^https?:\/\//i.test(value.trim()) ? value.trim() : `https://${value.trim()}`);
  if (!['https:', 'http:'].includes(url.protocol) || !url.hostname.includes('.') || url.username || url.password) throw new Error('Informe um endereço público válido.');
  return url.href;
}
export function mapsUrl(value: string): string {
  const url = new URL(webUrl(value));
  const google = /(^|\.)google\.(com|com\.br|[a-z]{2}|co\.[a-z]{2})$/.test(url.hostname);
  if (!(url.hostname === 'maps.app.goo.gl' || (url.hostname === 'goo.gl' && url.pathname.startsWith('/maps')) || (google && (url.pathname.startsWith('/maps') || url.hostname.startsWith('maps.'))))) throw new Error('Informe um link do Google Maps.');
  return url.href;
}
export function nameFromMaps(value: string): string {
  try {
    const part = new URL(mapsUrl(value)).pathname.match(/\/place\/([^/]+)/)?.[1];
    return part ? decodeURIComponent(part.replace(/\+/g, ' ')) : '';
  } catch { return ''; }
}
export function phoneDigits(value: string): string {
  if (/[a-z]/i.test(value)) throw new Error('Informe um telefone válido.');
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (/^0[1-9]\d{9,10}$/.test(digits)) digits = digits.slice(1);
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  if (!/^[1-9]\d{9,14}$/.test(digits) || (digits.startsWith('55') && !/^55[1-9]\d(?:\d{8}|9\d{8})$/.test(digits))) throw new Error('Informe DDD + número ou um telefone internacional com código do país.');
  return digits;
}
export function splitMessages(text: string): string[] { return text.split(/\r?\n+/).map(line => line.trim()).filter(Boolean); }
export function messagesFor(lead: Draft): string[] {
  const company = lead.name.trim() || 'sua empresa';
  const observation = lead.siteState === 'no' ? 'Não encontrei um site próprio de vocês. Vocês já têm um?' : lead.siteState === 'yes' && lead.problems.trim() ? `Ao olhar o site de vocês, anotei este ponto para revisar: ${lead.problems.trim()}` : 'Tenho uma ideia para apresentar os serviços de vocês e facilitar o contato pelo WhatsApp.';
  return [
    `Olá, é da ${company}?\nTudo bem?`,
    `Sou da MainCode e encontrei a ${company} pesquisando no Google.\n${observation}\nPosso te mandar uma ideia de como ficaria uma página com os serviços e contato de vocês?`,
    `Olá${lead.name.trim() ? `, pessoal da ${lead.name.trim()}` : ''}! Passando para retomar nossa conversa sobre o site. Ainda faz sentido conversar sobre isso? Se não for o momento, sem problema.`,
  ];
}
export function isLead(value: any): value is Lead {
  return value && typeof value.id === 'string' && ['mapsUrl', 'phone', 'name', 'address', 'website', 'problems', 'improvements', 'createdAt'].every(key => typeof value[key] === 'string') && ['unknown', 'no', 'yes'].includes(value.siteState) && ['novo', 'contatado', 'respondeu', 'proposta', 'perdido'].includes(value.status) && Array.isArray(value.messages) && value.messages.length === 3 && value.messages.every((message: unknown) => typeof message === 'string');
}
export function loadLeads(): Lead[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || !parsed.every(isLead)) throw new Error('Os dados salvos não puderam ser carregados. Faça um backup antes de continuar.');
    return parsed.map(lead => { try { return { ...lead, phone: `+${phoneDigits(lead.phone)}` }; } catch { return lead; } });
  }
  const old = JSON.parse(localStorage.getItem('maincode_prospeccao_leads_v1') || '[]');
  if (!Array.isArray(old)) throw new Error('Não foi possível carregar os leads anteriores.');
  return old.filter(item => item.id !== 'seed-1').map(item => {
    const draft: Draft = { ...emptyDraft, name: item.businessName || '', phone: item.whatsapp || item.phone || '', address: [item.city, item.country].filter(Boolean).join(', '), website: item.website || '', siteState: item.websiteQuality === 'sem_site' ? 'no' : item.website ? 'yes' : 'unknown', problems: item.painPoints || item.notes || '' };
    return { ...draft, id: String(item.id), status: item.status === 'qualificado' ? 'novo' : item.status || 'novo', createdAt: item.createdAt || new Date().toISOString(), messages: messagesFor(draft) };
  });
}
export async function analyzeWebsite(website: string, signal: AbortSignal): Promise<NonNullable<Lead['analysis']>> {
  const url = webUrl(website);
  const endpoint = new URL('https://www.googleapis.com/pagespeedonline/v5/runPagespeed');
  endpoint.searchParams.set('url', url); endpoint.searchParams.set('strategy', 'mobile'); endpoint.searchParams.set('locale', 'pt-BR');
  ['performance', 'accessibility', 'seo', 'best-practices'].forEach(category => endpoint.searchParams.append('category', category));
  const response = await fetch(endpoint.href, { signal });
  if (!response.ok) throw new Error(response.status === 429 ? 'O Google atingiu o limite de consultas. Tente mais tarde ou abra o relatório externo.' : 'O Google não conseguiu analisar este site. Revise o link ou tente mais tarde.');
  const result = (await response.json()).lighthouseResult;
  if (!result?.categories || !result?.audits) throw new Error('O Google não retornou uma análise válida.');
  const scores: Record<string, number> = {};
  Object.entries(result.categories).forEach(([key, category]: [string, any]) => { if (typeof category.score === 'number') scores[key] = Math.round(category.score * 100); });
  const findings = Object.values(result.audits as Record<string, any>).filter(audit => typeof audit.score === 'number' && audit.score < 0.9 && ['binary', 'numeric'].includes(audit.scoreDisplayMode) && typeof audit.title === 'string').sort((a, b) => a.score - b.score).slice(0, 6).map(audit => `${audit.title}${audit.displayValue ? ` (${audit.displayValue})` : ''}`);
  return { url, at: new Date().toISOString(), scores, findings };
}
